import { useEffect, useState } from "react";

import { kaiApi } from "@/services/kaiApi";
import type { KaiMailReviewContext } from "@/services/kaiApi";
import { KaiMailReviewSnapshot } from "./KaiMailReviewSnapshot";
import { parseMailReviewSnapshotSpec } from "./kaiMailReviewTypes";
import { KaiBlockLoading } from "./KaiBlockLoading";
import { KaiBlockError } from "./KaiBlockError";

type KaiMailReviewSnapshotSpec = NonNullable<
  ReturnType<typeof parseMailReviewSnapshotSpec>
>;

/**
 * Resolves a DB-backed `mail_review_snapshot` artifact by ID. During a live turn
 * the SSE payload may include the full card under `artifact`; on reload we fetch
 * it.
 */
export function KaiMailReviewSnapshotRef({
  artifactId,
  conversationId,
  cachedArtifact,
  onReviewEmail,
}: {
  artifactId: string;
  conversationId?: string | null;
  cachedArtifact?: unknown;
  onReviewEmail?: (ctx: KaiMailReviewContext, text: string) => void;
}) {
  const cachedSpec = cachedArtifact
    ? parseMailReviewSnapshotSpec(JSON.stringify(cachedArtifact))
    : null;

  const [spec, setSpec] = useState<KaiMailReviewSnapshotSpec | null>(cachedSpec);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (spec || !conversationId || !artifactId) return;
    let cancelled = false;
    (async () => {
      try {
        const artifact = await kaiApi.getArtifact(conversationId, artifactId);
        const parsed = parseMailReviewSnapshotSpec(
          JSON.stringify(artifact.payload),
        );
        if (cancelled) return;
        if (parsed) setSpec(parsed);
        else setFailed(true);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [spec, conversationId, artifactId]);

  if (spec)
    return <KaiMailReviewSnapshot spec={spec} onReviewEmail={onReviewEmail} />;
  if (failed) return <KaiBlockError tag="mail_review_snapshot" />;
  return <KaiBlockLoading label="Loading mail review…" />;
}
